import { useAuth, useRequest } from '../../../../hooks';
import { notify } from '../../../../app';
import {
  addScrapingTask,
  downloadScrapingTask,
  retryScrapingTask,
  cancelScrapingTask,
  bulkScrapingTasksExport,
  deleteScrapingTasks,
  refreshDemoTasks,
  uploadScreenshot,
} from '../../../../api';
import { FileTypes } from '../../../../services/scraper';
import {
  downloadCsvFile,
  convertTasksDetailsToCsv,
  isSuccessStatus,
} from '../utils';
import type { FormProps } from '../components/TaskForm';
import type { TaskPayload, TasksDetailsResponse } from '../../../../api';
import type { CompanyInfo } from '../components/BulkUploadPreview';
import type { TaskDetails } from '../../../../services/scraper';
import type { ScreenshotTypes } from '../../../../services/scraper';

const processStringValue = (string: string): string | undefined => {
  if (typeof string !== 'string') return string;
  return string?.length ? string : undefined;
};

export const useScrapingTasks = ({
  taskData,
  formKey,
  refresh,
  onClose,
  resetFormState,
  clearSelection,
  scrapingList,
  selectedTaskIds,
  isSelectAllMode,
  filters,
  setDeleteTaskId,
  onCloseDeleteModal,
  onCloseBulkDeleteModal,
}: {
  taskData: FormProps;
  formKey: string;
  refresh: () => void;
  onClose?: () => void;
  resetFormState: () => void;
  clearSelection: () => void;
  scrapingList: TasksDetailsResponse[];
  selectedTaskIds: string[];
  isSelectAllMode: boolean;
  filters: Record<string, unknown> | undefined;
  setDeleteTaskId: (id: string | null) => void;
  onCloseDeleteModal: () => void;
  onCloseBulkDeleteModal: () => void;
}) => {
  const { user } = useAuth();

  const [handleAddScrapingTask, addingScraping] = useRequest(
    async _signal => {
      const {
        agent_id,
        campaign_name,
        depth,
        take_screenshot,
        is_include_links,
        include_path_regex,
        exclude_path_regex,
        hard_parse,
        url,
        organisation_name,
        should_create_demo_bot,
        companies_info,
        page_limit,
      } = taskData;

      let finalCompaniesInfo: CompanyInfo[] = [];

      if (companies_info && companies_info.length > 0) {
        finalCompaniesInfo = companies_info;
      } else if (organisation_name && url) {
        finalCompaniesInfo = [
          {
            company_name: organisation_name,
            url,
          },
        ];
      } else if (!should_create_demo_bot) {
        finalCompaniesInfo = [
          {
            company_name: '',
            url,
          },
        ];
      } else {
        throw new Error(
          'Either upload a file with companies or provide Company Name and URL'
        );
      }

      const payload = {
        companies_info: finalCompaniesInfo,
        agent_id,
        campaign_name,
        depth,
        include_path_regex,
        exclude_path_regex,
        requested_by: user?.name,
        take_screenshot,
        page_limit: page_limit ?? 250,
        is_include_links,
        hard_parse,
        should_create_demo_bot,
      };

      return addScrapingTask(payload as TaskPayload);
    },
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('New Scraping task created');
        resetFormState();
      },
      onError: error => {
        const errorMessage =
          error?.message || 'Failed to create new Scraping task';
        notify.error(errorMessage);
      },
      deps: [formKey, taskData],
    }
  );

  const [handleretryScrapingTask, retringScrapingTask] = useRequest(
    (_signal, taskId: string) => retryScrapingTask(taskId),
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('Scraping task recreated successfully');
      },
      onError: () => {
        notify.error('Failed to recreate Scraping task');
      },
      deps: [],
    }
  );

  const [handleCancelScrapingTask, cancelingScrapingTask] = useRequest(
    (_signal, taskId: string) => cancelScrapingTask(taskId),
    {
      onSuccess: () => {
        refresh();
        onClose?.();
        notify.success('Scraping task camceled successfully');
      },
      onError: () => {
        notify.error('Failed to cancel task');
      },
      deps: [],
    }
  );

  const [handleDownloadScrapingTask, downloadingScraping] = useRequest(
    async (_signal, taskId: string, fileType: FileTypes = FileTypes.JSON) => {
      const blob = await downloadScrapingTask(taskId, fileType);

      if (!(blob instanceof Blob)) {
        notify.error('Invalid response received.');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = fileType === FileTypes.JSON ? 'json' : 'zip';
      a.download = `scraping-task-${Date.now()}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      notify.success('Scraping data downloaded successfully');
    },
    {
      onError: error => {
        notify.error('Failed to download Scraping data');
      },
      deps: [],
    }
  );

  const getExportableTaskIdsFromTasks = (
    taskList: TaskDetails[],
    taskIdsToFilter: string[] | null
  ): string[] | null => {
    if (taskIdsToFilter === null) {
      return taskList
        .filter(task => isSuccessStatus(task.status))
        .map(task => task.id)
        .filter(Boolean);
    }

    return taskIdsToFilter.filter(taskId => {
      const task = taskList.find(t => t.id === taskId);
      return task && isSuccessStatus(task.status);
    });
  };

  const [handleBulkScrapingTasksExport, bulkScrapingExport] = useRequest(
    async _signal => {
      const urlFilter = processStringValue(filters?.url as string) || null;
      const campaignFilter =
        processStringValue(filters?.campaign_name as string) || null;
      const currentTasks = scrapingList?.[0]?.tasks || [];

      const taskIdsToExport = isSelectAllMode
        ? null
        : getExportableTaskIdsFromTasks(currentTasks, selectedTaskIds);

      return await bulkScrapingTasksExport(
        taskIdsToExport,
        urlFilter,
        campaignFilter
      );
    },
    {
      onSuccess: response => {
        try {
          let csvContent: string;
          const currentTasks = scrapingList?.[0]?.tasks || [];

          const taskIdsForDownload =
            getExportableTaskIdsFromTasks(
              currentTasks,
              isSelectAllMode ? null : selectedTaskIds
            ) || [];

          if (response instanceof Blob) {
            response.text().then(text => {
              csvContent = text;
              downloadCsvFile(csvContent, taskIdsForDownload, clearSelection);
            });
            return;
          } else if (typeof response === 'string') {
            csvContent = response;
          } else {
            csvContent = convertTasksDetailsToCsv(response);
          }

          downloadCsvFile(csvContent, taskIdsForDownload, clearSelection);
        } catch (error) {
          notify.error('Failed to process scraping tasks data');
        }
      },
      onError: error => {
        console.error('Bulk download error:', error);
        notify.error('Failed to download scraping tasks');
      },
      deps: [selectedTaskIds, filters, isSelectAllMode, scrapingList],
    }
  );

  const [handleDeleteScrapingTasks, deletingScraping] = useRequest(
    async (_signal, taskIds: string[]) => {
      await deleteScrapingTasks(taskIds);
    },
    {
      onSuccess: () => {
        refresh();
        clearSelection();
        setDeleteTaskId(null);
        onCloseDeleteModal();
        onCloseBulkDeleteModal();
        notify.success('Scraping tasks deleted successfully');
      },
      onError: error => {
        const errorMessage =
          error?.message || 'Failed to delete scraping tasks';
        notify.error(errorMessage);
      },
      deps: [],
    }
  );

  const [handleRefreshDemoTasks, refreshingDemo] = useRequest(
    async (_signal, taskIds: string[] | null, campaignName?: string | null) => {
      await refreshDemoTasks(taskIds, campaignName);
    },
    {
      onSuccess: () => {
        refresh();
        clearSelection();
        notify.success('Demo tasks refreshed successfully');
      },
      onError: error => {
        const errorMessage = error?.message || 'Failed to refresh demo tasks';
        notify.error(errorMessage);
      },
      deps: [],
    }
  );

  const handleConfirmDelete = (deleteTaskId: string | null) => {
    if (deleteTaskId) {
      handleDeleteScrapingTasks([deleteTaskId]);
      setDeleteTaskId(null);
      onCloseDeleteModal();
    }
  };

  const handleConfirmBulkDelete = () => {
    if (selectedTaskIds.length > 0) {
      handleDeleteScrapingTasks(selectedTaskIds);
      onCloseBulkDeleteModal();
    }
  };

  const [handleUploadScreenshot, uploadingScreenshot] = useRequest(
    async (
      _signal,
      taskId: string,
      file: File,
      screenshotType: ScreenshotTypes
    ) => {
      const blob = new Blob([file], { type: file.type });
      await uploadScreenshot(taskId, blob, screenshotType);
    },
    {
      onSuccess: () => {
        refresh();
        notify.success('Screenshot uploaded successfully');
      },
      onError: error => {
        const errorMessage = error?.message || 'Failed to upload screenshot';
        notify.error(errorMessage);
      },
      deps: [],
    }
  );

  return {
    handleAddScrapingTask,
    addingScraping,
    handleretryScrapingTask,
    retringScrapingTask,
    handleCancelScrapingTask,
    cancelingScrapingTask,
    handleDownloadScrapingTask,
    downloadingScraping,
    handleBulkScrapingTasksExport,
    bulkScrapingExport,
    handleDeleteScrapingTasks,
    deletingScraping,
    handleRefreshDemoTasks,
    refreshingDemo,
    handleConfirmDelete,
    handleConfirmBulkDelete,
    getExportableTaskIdsFromTasks,
    handleUploadScreenshot,
    uploadingScreenshot,
  };
};
